import React, {useContext} from "react";
import Link from "next/link";
import Icon from "./Icon";
import Button from "./Button";
import { ASSET_URL } from "../../../config";
import renderIcon from "../../../components/renderIcon";
import {applyLang} from "../../../i18n";
import {TravelThemeContext} from "../index";
import { isExternalUrl, buildLinkHref, buildMenuUrl } from "../../../components/normalizeMenuUrl";

// Interne → <Link> Next (SSR + navigation client).
// Externe → <a> natif (Next <Link> ne gère pas bien les URLs cross-origin).
function HeroCta({ org, url, variant, children }) {
    if (!url) {
        return <Button variant={variant} size="lg" as="button">{children}</Button>;
    }
    const isExternal = isExternalUrl(url, org);
    if (isExternal) {
        return (
            <Button variant={variant} size="lg" as="a" href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                {children}
            </Button>
        );
    }
    return (
        <Link href={buildLinkHref(buildMenuUrl(url, org), org)}>
            <Button variant={variant} size="lg" as="span">
                {children}
            </Button>
        </Link>
    );
}

const heroStyles = `
.tr-hero {
  position: relative;
  margin-top: -60px;
  padding-top: 80px;
}
.tr-hero__frame {
  position: relative;
  height: clamp(440px, 62vh, 620px);
  overflow: hidden;
  border-bottom-left-radius: 40px;
  border-bottom-right-radius: 40px;
}
.tr-hero__img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  z-index: 0;
}
/* Cross-fade + léger zoom Ken-Burns-ish à chaque changement d'image.
   Utile surtout quand le contenu textuel reste figé (slides image-only) :
   l'image bouge, mais le texte ne "retape" pas d'animation d'entrée. */
.tr-hero__img--enter {
  animation: tr-hero-img-enter 1.2s cubic-bezier(.16,1,.3,1) both;
}
@keyframes tr-hero-img-enter {
  from { opacity: 0; transform: scale(1.04); }
  to   { opacity: 1; transform: scale(1);    }
}
.tr-hero__wash {
  position: absolute; inset: 0;
  background:
    linear-gradient(180deg, rgba(7,24,35,.55) 0%, rgba(7,24,35,0) 38%, rgba(7,24,35,.45) 100%),
    linear-gradient(92deg, rgba(7,24,35,.55) 0%, rgba(7,24,35,0) 55%);
  z-index: 1;
}
[dir="rtl"] .tr-hero__wash {
  background:
    linear-gradient(180deg, rgba(7,24,35,.55) 0%, rgba(7,24,35,0) 38%, rgba(7,24,35,.45) 100%),
    linear-gradient(-92deg, rgba(7,24,35,.55) 0%, rgba(7,24,35,0) 55%);
}

/* Content */
.tr-hero__content {
  position: absolute; inset: 0;
  z-index: 2;
  display: flex; flex-direction: column; justify-content: flex-end;
  padding: clamp(20px, 4vw, 48px) clamp(20px, 5vw, 56px) clamp(36px, 6vw, 64px);
  color: white;
  max-width: 760px;
  pointer-events: none;
}
.tr-hero__content--active {
  pointer-events: auto;
}

/* Animations luxueuses en cascade */
.tr-hero__content--active .tr-hero__eyebrow {
  animation: tr-enter 1s cubic-bezier(.16,1,.3,1) .15s both;
}
.tr-hero__content--active .tr-hero__title {
  animation: tr-enter 1.1s cubic-bezier(.16,1,.3,1) .35s both;
}
.tr-hero__content--active .tr-hero__sub {
  animation: tr-enter .95s cubic-bezier(.16,1,.3,1) .55s both;
}
.tr-hero__content--active .tr-hero__desc {
  animation: tr-enter .9s cubic-bezier(.16,1,.3,1) .7s both;
}
.tr-hero__content--active .tr-hero__ctas {
  animation: tr-enter .9s cubic-bezier(.16,1,.3,1) .88s both;
}

@keyframes tr-enter {
  from { opacity: 0; transform: translateY(32px); filter: blur(6px); }
  to   { opacity: 1; transform: translateY(0);    filter: blur(0);   }
}

/* Eyebrow */
.tr-hero__eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255,255,255,.14);
  border: 1px solid rgba(255,255,255,.22);
  backdrop-filter: blur(6px);
  font-size: 12px; font-weight: 600;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: rgba(255,255,255,.96);
  width: max-content;
}

/* Title */
.tr-hero__title {
  font-weight: 600;
  font-size: clamp(36px, 6.2vw, 68px);
  line-height: 1.02;
  letter-spacing: -.02em;
  margin: 14px 0 0;
  text-wrap: balance;
  width:100vw;
  text-shadow: 0 2px 12px rgba(0,0,0,.35), 0 1px 3px rgba(0,0,0,.25);
}
.tr-hero__title em {
  font-style: normal;
  display: block;
  color: color-mix(in oklab, white 88%, var(--secondary) 12%);
  font-weight: 500;
  font-size: .48em;
  letter-spacing: .02em;
  margin-top: 8px;
  opacity: .92;
}

/* Desc */
.tr-hero__desc {
  margin-top: 14px;
  font-size: 16px; line-height: 1.55;
  color: rgba(255,255,255,.82);
  max-width: 520px;
  text-wrap: pretty;
}

/* CTAs */
.tr-hero__ctas {
  margin-top: 24px;
  display: flex; flex-wrap: wrap; gap: 10px;
}

/* Dots */
.tr-hero__dots {
  position: absolute;
  z-index: 3;
  bottom: clamp(14px, 3vw, 24px);
  inset-inline-end: clamp(16px, 4vw, 40px);
  display: flex; gap: 7px; align-items: center;
}
.tr-hero__dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: rgba(255,255,255,.4);
  border: none; padding: 0; cursor: pointer;
  transition: background .2s, transform .2s;
}
.tr-hero__dot--active {
  background: white;
  transform: scale(1.3);
}

@media (max-width: 640px) {
  .tr-hero__frame {
    border-bottom-left-radius: 28px;
    border-bottom-right-radius: 28px;
    height: 60vh;
  }
  .tr-hero__content {
    padding: 24px 22px 60px;
    max-width: 100%;
  }
  .tr-hero__title {
    font-size: clamp(32px, 9vw, 52px);
    max-width: 80%;
  }
  .tr-hero__desc {
    font-size: 15px;
    max-width: 88%;
  }
  .tr-hero__eyebrow { font-size: 11px; }
}
`;


function SlideContent({ slide }) {

    const context=useContext(TravelThemeContext);

    slide=applyLang(slide,context.lang)
    const eyebrowIcon = renderIcon(slide.eyebrow_icon);
    const btn1Icon    = renderIcon(slide.btn1_icon);
    const btn2Icon    = renderIcon(slide.btn2_icon);

    return (
        <div className="tr-hero__content tr-hero__content--active">
            {slide.eyebrow && (
                <span className="tr-hero__eyebrow">
                    {eyebrowIcon}
                    {slide.eyebrow}
                </span>
            )}

            {slide.title && (
                <h1 className="tr-hero__title">
                    {slide.title}

                    {slide.subtitle && (
                        <em>{slide.subtitle}</em>
                    )}
                </h1>
            )}

            <p className="tr-hero__desc">
                {slide.description || "\u00a0"}
            </p>

            {(slide.btn1_label || slide.btn2_label) && (
                <div className="tr-hero__ctas">
                    {slide.btn1_label && (
                        <HeroCta org={context.org} url={slide.btn1_url} variant="primary">
                            {btn1Icon}
                            {slide.btn1_label}
                        </HeroCta>
                    )}
                    {slide.btn2_label && (
                        <HeroCta org={context.org} url={slide.btn2_url} variant="secondary">
                            {btn2Icon}
                            {slide.btn2_label}
                        </HeroCta>
                    )}
                </div>
            )}
        </div>
    );
}

// Vrai si la slide apporte du contenu textuel/CTA à afficher.
// Une slide "image-only" (rien à part l'image) laisse le contenu de la slide précédente visible.
function slideHasContent(slide, lang) {
    if (!slide) return false;
    const s = applyLang(slide, lang);
    return !!(
        s.eyebrow ||
        s.title ||
        s.subtitle ||
        s.description ||
        s.btn1_label ||
        s.btn2_label
    );
}

export default function Hero({ slides, fullWidth = false }) {
    const context = useContext(TravelThemeContext);
    const validSlides = (slides || []).filter(s => s?.image);
    const [current, setCurrent] = React.useState(0);

    React.useEffect(() => {
        if (validSlides.length <= 1) return;
        const timer = setInterval(() => {
            setCurrent(i => (i + 1) % validSlides.length);
        }, 10 * 1000);
        return () => clearInterval(timer);
    }, [validSlides.length]);

    if (!validSlides.length) return null;

    // Contenu "sticky" : on remonte en arrière (avec wrap circulaire) jusqu'à trouver
    // la dernière slide qui a du contenu. Si aucune n'en a, on ne rend pas de contenu.
    // L'ordre des slides dicte donc quelle slide "porte" le texte pour la séquence suivante.
    const contentIndex = (() => {
        const n = validSlides.length;
        for (let k = 0; k < n; k++) {
            const idx = (current - k + n) % n;
            if (slideHasContent(validSlides[idx], context.lang)) return idx;
        }
        return -1;
    })();

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: heroStyles }} />
            <section className={`tr-hero${fullWidth ? " tr-hero--full" : ""}`}>
                <div className={fullWidth ? undefined : "travel-container"}>
                    <div className="tr-hero__frame">

                        <img
                            key={`img-${current}`}
                            src={`${ASSET_URL}${validSlides[current].image}`}
                            alt={validSlides[current].title || ""}
                            loading="eager"
                            className="tr-hero__img tr-hero__img--enter"
                        />

                        <div className="tr-hero__wash" />

                        {/* key = index du contenu (pas de la slide courante) → pas de re-render
                            des animations quand on passe sur des slides image-only successives.
                            On préfixe pour ne pas collisionner avec la key de l'img ci-dessus
                            quand current === contentIndex. */}
                        {contentIndex !== -1 && (
                            <SlideContent key={`content-${contentIndex}`} slide={validSlides[contentIndex]} />
                        )}

                        {/* Dots carrousel */}
                        {validSlides.length > 1 && (
                            <div className="tr-hero__dots">
                                {validSlides.map((_, i) => (
                                    <button
                                        key={i}
                                        className={`tr-hero__dot${i === current ? " tr-hero__dot--active" : ""}`}
                                        onClick={() => setCurrent(i)}
                                        aria-label={`Slide ${i + 1}`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </>
    );
}
